/**
 * `<SeriesRail>` — the clock made spatial (components.md §2.7).
 *
 * **Six chips, fixed: two behind, the live one, three ahead.** `N_ahead = 3`
 * (D4) sets the right-hand half; the two behind are the markets whose outcomes
 * the user just watched, which is the rail's other job — "what were the last
 * ones?". It does not scroll and it does not paginate: at 1,440 markets per
 * asset per day an open-ended rail is an unbounded query with a scrollbar
 * attached.
 *
 * **Four below 768px — one behind, the live one, two ahead** (`compact`), and
 * at every width a chip is the token's 92px or the rail's share of its
 * container, whichever is narrower. Six chips at a fixed 92px were 569px with
 * the caller's padding: wider than a phone, which panned the whole page, and
 * wider than the chart's own column at 768px. The window below still holds the
 * full frame; `compact` only changes how much of it the viewport shows.
 *
 * **The window is a function of the live anchor, not of what has arrived.** This
 * is the fix for the shape the rail had until now, where it rendered whatever
 * `series.{id}` happened to be holding and slid a viewport over it: the snapshot
 * carries five markets, a future market is only announced when it transitions,
 * and so the rail ran out of chips on the right — one fewer every minute — while
 * the live chip drifted left. So `railWindow()` builds every anchor from
 * `current − 2` to `current + 3` and fills the ones nothing has described yet
 * with a **placeholder carrying that anchor's real `market_id`**, which the
 * client is entitled to compute (`shiftMarketId`, components.md §2.7). The
 * placeholder is keyed the same as the market it stands in for, so the frame
 * that finally describes it updates a chip rather than replacing one.
 *
 * **A placeholder never claims a state it does not have.** Ahead of the live
 * anchor `SCHEDULED` is the honest answer — that is precisely what a market that
 * has not reached its strike window is. Behind it there is no honest answer, so
 * the chip renders its minute and no pill rather than inventing a `SETTLED` the
 * venue never sent. That case is transient: `GET /series/{id}/markets` seeds both
 * past chips at cold load and every later one arrives as a lifecycle event.
 *
 * **The advance is transform-only, and that is the whole implementation.** Chips
 * move left by exactly one slot every 60 seconds — never a cross-fade, never a
 * re-sort. Because it happens sixty times an hour, a version that cost a layout
 * would make the product feel broken all day. So the track holds a small buffer
 * of chips in ascending anchor order, each keyed by `market_id` so React never
 * recreates one, and the *only* thing that changes on a roll is a `translateX`
 * on the track.
 *
 * Trimming the buffer would move everything, so it is done with the transition
 * suppressed for exactly one frame: drop `k` chips off the front, subtract `k`
 * slots from the offset, and the net visual change is zero. That suppression is
 * newly load-bearing — the buffer used to be fed five chips and never reached
 * its bound, so a trim had never actually happened; now one chip is appended
 * every minute and it will.
 *
 * **There is still no tie-line, and since §F26 the reason has changed.**
 *
 * Until §F24 the resolved-previous chip's settlement price *was* the live
 * market's strike, and a 1px `border-strong` rule between the chips said so
 * without prose. §F24 made the two windows disjoint, so the rule would have
 * asserted a falsehood about money, and it was removed.
 *
 * §F26 made them **the same window again** — both are backward-looking and
 * `expiry(N) == anchor(N+1)` — so the rule would now be *true*. It stays out
 * anyway, for a different and weaker reason: the two chips' values coincide, but
 * the two *frames* do not arrive together (`market_opened(N+1)` fires at expiry,
 * `market_resolved(N)` about two seconds later), so a rule drawn between them
 * would be absent, then present, on every roll. A line that flickers sixty times
 * an hour teaches nothing.
 *
 * If it is ever wanted back, the thing to render is the shared `twap_window_id`,
 * which is on both payloads and is identical — not an inferred adjacency.
 */

import { useLayoutEffect, useMemo, useRef, type CSSProperties } from 'react'
import { Glyph } from '@/components/primitives'
import { MarketStatusPill } from './MarketStatusPill'
import { cx } from '@/lib/cx'
import { buildMarketId, parseMarketId, isMarketId } from '@/lib/market-id'
import type { MarketState } from '@/domain/lifecycle'

/** Markets kept behind the live one. The two the user just watched resolve. */
export const RAIL_SLOTS_BEFORE = 2
/** Markets kept ahead of it. `N_ahead = 3` is D4 — the rail cannot show a
 *  market the venue has not created. */
export const RAIL_SLOTS_AFTER = 3
/** The viewport, in chips. */
export const RAIL_SLOTS = RAIL_SLOTS_BEFORE + 1 + RAIL_SLOTS_AFTER

/** Below 768px: the market just resolved, and the next two. */
export const RAIL_SLOTS_BEFORE_COMPACT = 1
export const RAIL_SLOTS_AFTER_COMPACT = 2
export const RAIL_SLOTS_COMPACT = RAIL_SLOTS_BEFORE_COMPACT + 1 + RAIL_SLOTS_AFTER_COMPACT

/**
 * How many chips the track is shifted left by: enough to put the live chip in
 * slot `before`, always — which is what makes the chips behind it and the ones
 * ahead a fixed frame rather than whatever happens to have arrived. With no live
 * chip to centre on, the last `slots` of what arrived.
 */
export function railOffset(
  chips: readonly RailChip[],
  currentMarketId: string | null,
  before: number,
  slots: number,
): number {
  const current = chips.findIndex((c) => c.market_id === currentMarketId)
  return Math.max(0, current < 0 ? chips.length - slots : current - before)
}

/**
 * Chips kept in the DOM off-screen before the buffer is trimmed.
 *
 * Six rolls' worth of headroom past the viewport, so a trim — which costs the
 * one-frame transition suppression below — happens every sixth minute rather
 * than every minute.
 */
const BUFFER = RAIL_SLOTS + 6

export interface RailChip {
  market_id?: string
  state?: MarketState
  anchor_ms?: number
  expiry_at_ms?: number
  last_price_cents?: number | null
  outcome?: 'UP' | 'DOWN' | 'VOID' | null
  strike?: unknown
  /**
   * This slot's anchor exists on the grid and nothing has described it yet.
   *
   * Set by `railWindow()` only. It is not a wire field and never reaches one.
   */
  placeholder?: boolean
}

export interface SeriesRailProps {
  /** `markets[]` off `series.{id}` — in any order; the window sorts. */
  chips: readonly RailChip[]
  /** The market the page is pointed at. Its anchor is what the window centres
   *  on, so without it the rail can only render what it was handed. */
  currentMarketId: string | null
  /** `period_seconds`, off the series object. Never a constant — the anchor
   *  grid is the venue's (invariant 11). */
  periodS: number
  /**
   * The live market's UP price, in cents.
   *
   * It comes from `market.{id}` rather than from the rail's own channel, per
   * §5.4 — `series.{id}` sends a rail snapshot once and then lifecycle *events*,
   * and an event carries no price. Without this the live chip reads `—` from
   * the moment it opens, which is the one chip whose price matters.
   */
  livePriceCents?: number | null
  nowMs: number
  onSelect?: (marketId: string) => void
  /** Four chips rather than six — below 768px, where six do not fit. */
  compact?: boolean
  className?: string
}

export function SeriesRail({
  chips,
  currentMarketId,
  periodS,
  livePriceCents = null,
  nowMs,
  onSelect,
  compact = false,
  className,
}: SeriesRailProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  /** The first chip's anchor at the last paint. A change means the buffer was
   *  trimmed, which is the one case the advance must not animate. */
  const firstAnchorRef = useRef<number | null>(null)

  /**
   * The buffer, derived rather than stored.
   *
   * An earlier shape held it in state and refreshed it from an effect, which is
   * a cascading render on every `series.{id}` event — and the React Compiler
   * rules reject it. It does not need to be state: the buffer is a pure
   * function of the live anchor and the window the server last sent, and the
   * only thing that has to persist across renders is chip *identity*, which
   * React already gets from the `key`.
   */
  const railChips = useMemo(
    () => railWindow(chips, currentMarketId, periodS),
    [chips, currentMarketId, periodS],
  )

  const before = compact ? RAIL_SLOTS_BEFORE_COMPACT : RAIL_SLOTS_BEFORE
  const slots = compact ? RAIL_SLOTS_COMPACT : RAIL_SLOTS
  const offset = railOffset(railChips, currentMarketId, before, slots)

  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return
    // The one write. The advance happens sixty times an hour and it has to be a
    // transform and nothing else — no width change, no re-sort, no cross-fade —
    // because a version that cost a layout would make the product feel broken
    // all day.
    const firstAnchor = railChips[0]?.anchor_ms ?? null
    const trimmed = firstAnchorRef.current !== null && firstAnchor !== firstAnchorRef.current
    firstAnchorRef.current = firstAnchor
    if (!trimmed) {
      track.style.transform = translateFor(offset)
      return
    }
    // A trim drops `k` chips off the front and subtracts `k` from the offset, so
    // the net visual change is zero — but only if the transform does not
    // animate its way there. One frame without the transition, then back.
    track.style.transition = 'none'
    track.style.transform = translateFor(offset)
    void track.offsetWidth
    track.style.transition = ''
  }, [offset, railChips])

  if (railChips.length === 0) {
    return (
      <div className={cx('grid place-items-center py-3', className)}>
        <span className="text-body-sm text-text-tertiary">no scheduled markets</span>
      </div>
    )
  }

  return (
    // **The outer box is a size container and carries the caller's padding;
    // the inner one is the viewport and is exactly `slots` chips wide.** Two
    // boxes because the viewport's width is a count of chips — the caller's
    // `p-1_5` subtracted out of it (Tailwind's `border-box`) left the last
    // chip a sliver — while the chip's width has to come from the space the
    // caller actually has, which only a container can report.
    <div className={cx('[container-type:inline-size]', className)}>
      <div
        className="overflow-hidden"
        style={
          {
            // `100cqw` resolves where the variable is *used* — on this box and
            // on every chip — and both find the outer box as their container.
            '--rail-chip': `min(var(--ud-rail-chip-width), calc((100cqw - ${slots - 1}px) / ${slots}))`,
            width: `calc(var(--rail-chip) * ${slots} + ${slots - 1}px)`,
          } as CSSProperties
        }
        role="list"
        aria-label="upcoming markets"
      >
        <div
          ref={trackRef}
          className="flex gap-px transition-transform duration-[var(--ud-duration-base)] ease-out will-change-transform"
        >
          {railChips.map((chip, i) => (
            <Chip
              key={chip.market_id ?? i}
              chip={
                chip.market_id === currentMarketId && livePriceCents !== null
                  ? { ...chip, last_price_cents: livePriceCents }
                  : chip
              }
              nowMs={nowMs}
              live={chip.market_id === currentMarketId}
              {...(onSelect ? { onSelect } : {})}
            />
          ))}
        </div>
      </div>
    </div>
  )

  function translateFor(by: number): string {
    return `translateX(calc((var(--rail-chip) + 1px) * ${-by}))`
  }
}

// ---------------------------------------------------------------------------

function Chip({
  chip,
  nowMs,
  live,
  onSelect,
}: {
  chip: RailChip
  nowMs: number
  live: boolean
  onSelect?: (marketId: string) => void
}) {
  const state = chip.state

  return (
    <button
      type="button"
      role="listitem"
      // Clicking a future chip pre-loads that market (§2.7). In Phase 4 that is
      // a navigation; the prefetch choreography is Phase 7's.
      onClick={chip.market_id && onSelect ? () => onSelect(chip.market_id as string) : undefined}
      data-live={live ? 'true' : undefined}
      data-placeholder={chip.placeholder ? 'true' : undefined}
      className={cx(
        'relative flex shrink-0 flex-col items-start gap-0_5 border border-transparent px-1_5 py-1 text-left',
        'w-[var(--rail-chip)]',
        'hover:bg-bg-hover focus-visible:outline-none',
        'data-[live=true]:border-accent-border data-[live=true]:bg-bg-active',
      )}
    >
      <span
        className={cx(
          'ud-num text-caption',
          state === undefined ? 'text-text-tertiary' : 'text-text-primary',
        )}
      >
        {clockOf(chip.anchor_ms)}
      </span>

      <span className="ud-num text-micro text-text-secondary">
        {chip.outcome === 'UP' || chip.outcome === 'DOWN' ? (
          <span className={chip.outcome === 'UP' ? 'text-up-text' : 'text-down-text'}>
            <Glyph name={chip.outcome === 'UP' ? 'up' : 'down'} /> {chip.outcome}
          </span>
        ) : chip.last_price_cents != null ? (
          `${chip.last_price_cents}¢`
        ) : (
          '—'
        )}
      </span>

      {/* A past slot nothing has described gets the pill's height and no pill.
          Inventing a terminal state for it would be a claim about how somebody's
          money was decided, made by a component with no evidence. */}
      {state === undefined ? (
        <span aria-hidden="true" className="h-[var(--ud-control-height-sm)]" />
      ) : (
        <MarketStatusPill
          state={state}
          outcome={chip.outcome === 'VOID' ? null : (chip.outcome ?? null)}
          nowMs={nowMs}
          className="scale-90 origin-left"
        />
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------

/**
 * The rendered window: every anchor from `current − 2` to `current + 3`,
 * deduplicated, ascending, gap-free, and bounded.
 *
 * **Gap-free is the property the whole component rests on.** The track is a
 * flex row of fixed-width chips and the offset is an index, so a missing anchor
 * would put the live chip in the wrong slot and the roll would move by the wrong
 * distance. Filling the gaps is therefore not cosmetic — it is what makes
 * "never a re-sort" true at the render layer.
 *
 * Chips the venue sent *outside* the window are kept rather than dropped: a
 * fourth market ahead, or the ones that have scrolled off the left, cost a DOM
 * node each and are what the buffer is for. Only the front is trimmed, and only
 * past the point where trimming could reach the visible window.
 *
 * With no live `market_id` — before the cold load resolves — there is no anchor
 * to centre on, so this degrades to what the rail did before: sort what arrived
 * and render it.
 */
export function railWindow(
  incoming: readonly RailChip[],
  currentMarketId: string | null,
  periodS: number,
): RailChip[] {
  const byAnchor = new Map<number, RailChip>()
  for (const chip of incoming) {
    if (!chip.market_id) continue
    const anchorS = anchorOf(chip)
    if (anchorS === null) continue
    byAnchor.set(anchorS, { ...byAnchor.get(anchorS), ...chip, ...outcomeOf(chip) })
  }

  const live = currentMarketId !== null && isMarketId(currentMarketId) ? currentMarketId : null
  if (live === null || !Number.isInteger(periodS) || periodS <= 0) {
    return [...byAnchor.values()]
      .sort((a, b) => (a.anchor_ms ?? 0) - (b.anchor_ms ?? 0))
      .slice(-BUFFER)
  }
  const centre = parseMarketId(live).anchorS

  const anchors = [...byAnchor.keys()]
  // The window is at least `current ± the two halves`; anything the venue sent
  // beyond it widens the range rather than being thrown away — but only as far
  // as the buffer, because the loop below walks the range one anchor at a time
  // and a rail holding a chip from an hour ago would walk sixty of them.
  const from = Math.max(
    centre - BUFFER * periodS,
    Math.min(centre - RAIL_SLOTS_BEFORE * periodS, ...anchors),
  )
  const to = Math.min(
    centre + BUFFER * periodS,
    Math.max(centre + RAIL_SLOTS_AFTER * periodS, ...anchors),
  )

  const out: RailChip[] = []
  for (let anchorS = from; anchorS <= to; anchorS += periodS) {
    const held = byAnchor.get(anchorS)
    out.push(held ?? placeholderAt(live, anchorS, centre, periodS))
  }

  // Trim from the front only, and never into the visible window: the live chip
  // must keep `RAIL_SLOTS_BEFORE` chips behind it whatever the buffer does.
  const keepFrom = Math.max(0, out.length - BUFFER)
  const liveIndex = (centre - from) / periodS
  return out.slice(Math.min(keepFrom, Math.max(0, liveIndex - RAIL_SLOTS_BEFORE)))
}

/**
 * A chip for an anchor on the grid that nothing has described.
 *
 * The `market_id` is computed, which is D4's whole point — the id is
 * deterministic so the client can name a market before it is told about one — and
 * it is what lets the real frame land on this chip rather than beside it.
 *
 * `state` is set **only** ahead of the live anchor, where `SCHEDULED` is not a
 * guess but the definition of a market that has not reached its strike window.
 * Behind it, the chip carries no state at all; see `<Chip>`.
 */
function placeholderAt(
  currentMarketId: string,
  anchorS: number,
  centreAnchorS: number,
  periodS: number,
): RailChip {
  const { seriesId } = parseMarketId(currentMarketId)
  return {
    market_id: buildMarketId(seriesId, anchorS),
    anchor_ms: anchorS * 1000,
    expiry_at_ms: (anchorS + periodS) * 1000,
    ...(anchorS > centreAnchorS ? { state: 'SCHEDULED' as MarketState } : {}),
    placeholder: true,
  }
}

/**
 * The chip's outcome, from whichever of the two shapes it arrived in.
 *
 * `series.{id}` flattens it — `SeriesRailEntry.outcome` — while a `Market` off
 * `GET /series/{id}/markets` nests it at `resolution.outcome`, and the rail is
 * fed by both. Binding to the flat name alone is why a REST-seeded resolved chip
 * read `—`: the field was there and spelled the other way.
 */
function outcomeOf(chip: RailChip): Pick<RailChip, 'outcome'> | Record<string, never> {
  if (chip.outcome !== undefined && chip.outcome !== null) return { outcome: chip.outcome }
  const nested = (chip as { resolution?: { outcome?: unknown } }).resolution?.outcome
  if (nested === 'UP' || nested === 'DOWN' || nested === 'VOID') return { outcome: nested }
  return {}
}

/** A chip's anchor in seconds — off `anchor_ms` where it has one, and off its
 *  own id where it does not, because the id carries the anchor by construction. */
function anchorOf(chip: RailChip): number | null {
  if (typeof chip.anchor_ms === 'number') return Math.floor(chip.anchor_ms / 1000)
  if (chip.market_id && isMarketId(chip.market_id)) return parseMarketId(chip.market_id).anchorS
  return null
}

/** `21:46` — the anchor's wall-clock minute, which is the chip's whole name. */
function clockOf(anchorMs: number | undefined): string {
  if (anchorMs === undefined) return '—'
  const date = new Date(anchorMs)
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
}
